import { getSidenavNotificationsButtonSx } from '@app/components/layout/sidenav/sidenav-colors';
import { getSidenavSidebarPopoverPosition } from '@app/components/layout/sidenav/sidenav-popover-utils';
import { AnchorPosition } from '@app/core/hooks';
import NotificationsNoneOutlinedIcon from '@mui/icons-material/NotificationsNoneOutlined';
import NotificationsOutlinedIcon from '@mui/icons-material/NotificationsOutlined';
import { Badge, IconButton } from '@mui/material';
import { useRef, useState } from 'react';
import { SidenavNotificationsPopover } from './SidenavNotificationsPopover';

export type SidenavNotificationsButtonProps = {
  /** Unread count for the badge. Wired to Novu later; defaults to 0. */
  count?: number;
};

/**
 * Notification bell for the identity row. Opens an empty-state popover on click.
 * Empty state uses a quieter outlined glyph; unread uses a fuller icon + badge.
 */
export function SidenavNotificationsButton({ count = 0 }: SidenavNotificationsButtonProps) {
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);
  const [anchorPosition, setAnchorPosition] = useState<AnchorPosition | undefined>();
  const hasNotifications = count > 0;
  const Icon = hasNotifications ? NotificationsOutlinedIcon : NotificationsNoneOutlinedIcon;

  const handleToggle = () => {
    if (!open && buttonRef.current) {
      setAnchorPosition(getSidenavSidebarPopoverPosition(buttonRef.current) ?? undefined);
    }
    setOpen((prev) => !prev);
  };

  return (
    <>
      <IconButton
        ref={buttonRef}
        size="small"
        onClick={handleToggle}
        sx={(theme) => getSidenavNotificationsButtonSx(theme, { hasNotifications })}
      >
        <Badge
          color="error"
          variant={hasNotifications ? 'dot' : 'standard'}
          invisible={!hasNotifications}
          overlap="circular"
        >
          <Icon sx={{ fontSize: 18 }} />
        </Badge>
      </IconButton>
      <SidenavNotificationsPopover
        open={open}
        anchorPosition={anchorPosition}
        onClose={() => setOpen(false)}
      />
    </>
  );
}
